import { Logger } from "@logtail/next";

const isDev = process.env.NODE_ENV === "development";

const baseLogger = new Logger({ source: "slider-app" });

/**
 * Wrapper that logs to console in development and always sends to Better Stack.
 */
function createDevLogger(log: Logger): Logger {
  if (!isDev) return log;

  // Create a proxy that intercepts log methods to also console.log
  return new Proxy(log, {
    get(target, prop) {
      const value = target[prop as keyof Logger];

      // Intercept log methods
      if (
        typeof value === "function" &&
        ["info", "warn", "error", "debug"].includes(prop as string)
      ) {
        return (message: string, context?: Record<string, unknown>) => {
          // Console log with nice formatting
          const level = (prop as string).toUpperCase();
          const category = (target as unknown as { _fields?: { category?: string } })._fields
            ?.category;
          const prefix = category ? `[${level}:${category}]` : `[${level}]`;
          if (context && Object.keys(context).length > 0) {
            console.log(prefix, message, context);
          } else {
            console.log(prefix, message);
          }
          // Also send to Better Stack
          return (value as (m: string, c?: Record<string, unknown>) => unknown).call(
            target,
            message,
            context,
          );
        };
      }

      // For .with(), wrap the result too
      if (prop === "with" && typeof value === "function") {
        return (context: Record<string, unknown>) => {
          const childLogger = (value as (c: Record<string, unknown>) => Logger).call(
            target,
            context,
          );
          return createDevLogger(childLogger);
        };
      }

      return value;
    },
  });
}

/**
 * Better Stack logger singleton.
 * In development, also logs to console for visibility.
 *
 * Usage:
 *   logger.info("Something happened", { extra: "context" });
 *
 * With context (for filtering in Better Stack):
 *   const log = logger.with({ category: "coding-agent", projectId });
 *   log.info("Agent started");
 */
export const logger = createDevLogger(baseLogger);
